import {apiRequestWithAuth} from "@helpers/query/apiRequestWithAuth";
import {OnShowError} from "@baseTypes/base";
import {BASE_TRACK_API_URL} from "@constants/config";
import {
    BodyAddTaskSingleVoyage,
    BodyAddTasksSingleVoyage,
    BodySaveTaskSingleVoyage,
    BodySaveTasksSingleVoyage,
    BodyUpdateTasksSingleVoyage,
    DataTasksSingleVoyage,
    IBodyUpdateTaskSingleVoyage,
    IParamsDeleteTaskSingleVoyage,
    IPropsChangeExecutionStatus,
    IPropsCloseTaskSingleVoyage,
    IPropsDeleteTaskFileSingleVoyage,
    IPropsGetTasksMapByVoyageIdsSingleVoyage,
    IPropsGetTasksSingleVoyage,
    IPropsUploadTaskFileSingleVoyage,
    ITaskExecutionSingleVoyage,
    ITaskSingleVoyage,
    MapTasksByVoyages,
    TaskCreationSingleVoyage
} from "@containers/singleVoyage/tasks/types";


const BASE_URL = `${BASE_TRACK_API_URL}/v1/accounts`

export const saveTaskSingleVoyage = async (
    body: BodySaveTaskSingleVoyage,
    onError?: OnShowError
): Promise<ITaskSingleVoyage> => {
    if ('id' in body && body.id) {
        return updateTaskSingleVoyage(body as IBodyUpdateTaskSingleVoyage, onError)
    }
    return addTaskSingleVoyage(body, onError)

}

export const addTaskSingleVoyage = async (
    {
        accountId,
        voyageId,
        ...body
    }: BodyAddTaskSingleVoyage,
    onError?: OnShowError
): Promise<ITaskSingleVoyage> => {
    const url = `${BASE_URL}/${accountId}/single-voyages/${voyageId}/task`
    return await apiRequestWithAuth<ITaskSingleVoyage>({
        url,
        options: {
            method: "POST",
            body: JSON.stringify(taskPlanBody(body)),
            headers: {
                'Content-type': 'application/json'
            }

        },
        onError
    });

}

export const updateTaskSingleVoyage = async ({
                                                 accountId,
                                                 voyageId,
                                                 ...body
                                             }: IBodyUpdateTaskSingleVoyage,
                                             onError?: OnShowError
): Promise<ITaskSingleVoyage> => {
    const url = `${BASE_URL}/${accountId}/single-voyages/${voyageId}/tasks/${body.id}`
    return await apiRequestWithAuth<ITaskSingleVoyage>({
        url,
        options: {
            method: "PUT",
            body: JSON.stringify(taskPlanBody(body)),
            headers: {
                'Content-type': 'application/json'
            }

        },
        onError
    });

}

export const saveTasksSingleVoyage = async (
    body: BodySaveTasksSingleVoyage,
    onError?: OnShowError
): Promise<ITaskSingleVoyage[]> => {
    if (!body?.tasks?.length) {
        return []
    }
    const addedTasks: TaskCreationSingleVoyage[] = []
    const updatedTasks: (TaskCreationSingleVoyage & { id: number })[] = []
    body.tasks.forEach((task) => {
        if (task.id) {
            updatedTasks.push(task as (TaskCreationSingleVoyage & { id: number }))
            return
        }
        addedTasks.push(task)
    })
    const res: ITaskSingleVoyage[] = []
    if (addedTasks?.length >= 1) {
        const newTasks = await addTasksSingleVoyage({
            accountId: Number(body.accountId),
            voyageId: body.voyageId,
            tasks: addedTasks,
        }, onError)
        if (newTasks?.length >= 1) {
            res.push(...newTasks)
        }
    }
    if (updatedTasks?.length >= 1) {
        const newTasks = await updateTasksSingleVoyage({
            accountId: Number(body.accountId),
            voyageId: body.voyageId,
            tasks: updatedTasks,
        }, onError)
        if (newTasks?.length >= 1) {
            res.push(...newTasks)
        }
    }
    return res;

}


export const addTasksSingleVoyage = async (
    {
        accountId,
        voyageId,
        ...body
    }: BodyAddTasksSingleVoyage,
    onError?: OnShowError
): Promise<ITaskSingleVoyage[]> => {
    const url = `${BASE_URL}/${accountId}/single-voyages/${voyageId}/tasks`
    return await apiRequestWithAuth<ITaskSingleVoyage[]>({
        url,
        options: {
            method: "POST",
            body: JSON.stringify(body.tasks.map(taskPlanBody)),
            headers: {
                'Content-type': 'application/json'
            }

        },
        onError
    });

}

export const updateTasksSingleVoyage = async ({
                                                  accountId,
                                                  voyageId,
                                                  ...body
                                              }: BodyUpdateTasksSingleVoyage,
                                              onError?: OnShowError
): Promise<ITaskSingleVoyage[]> => {
    const url = `${BASE_URL}/${accountId}/single-voyages/${voyageId}/tasks`
    return await apiRequestWithAuth<ITaskSingleVoyage[]>({
        url,
        options: {
            method: "PUT",
            body: JSON.stringify(body.tasks.map(taskPlanBody)),
            headers: {
                'Content-type': 'application/json'
            }

        },
        onError
    });

}

export const deleteTaskSingleVoyage = async ({
                                                 accountId,
                                                 voyageId,
                                                 id,
                                             }: IParamsDeleteTaskSingleVoyage,
                                             onError?: OnShowError
) => {
    const url = `${BASE_URL}/${accountId}/single-voyages/${voyageId}/tasks/${id}`
    return await apiRequestWithAuth<ITaskSingleVoyage>({
        url,
        options: {
            method: "DELETE",
        },
        onError
    });

}


export const getTasksSingleVoyage = async (
    {accountId, voyageId}: IPropsGetTasksSingleVoyage,
    onError?: OnShowError
): Promise<DataTasksSingleVoyage> => {
    const url = `${BASE_URL}/${accountId}/single-voyages/${voyageId}/tasks`
    return await apiRequestWithAuth<DataTasksSingleVoyage>({
        url,
        options: {
            method: "GET",
        },
        onError
    });

}


export const getTasksMapByVoyageIdsSingleVoyage = async (
    {accountId, voyageIds}: IPropsGetTasksMapByVoyageIdsSingleVoyage,
): Promise<MapTasksByVoyages> => {

    const mapTasks: MapTasksByVoyages = {}

    for (const voyageId of voyageIds) {
        const tasks = await getTasksSingleVoyage({
            accountId,
            voyageId
        })
        if (!tasks?.length) {
            continue
        }
        mapTasks[voyageId] = tasks
    }

    return mapTasks

}


/**
 * Closing the work at a single stop.
 *
 * Idempotent: repeating the same status returns 200 with no change.
 * A 409 means the execution was already closed differently - do not retry.
 * version is not sent: conflicts are decided by state, not by version.
 */
export const changeExecutionStatusSingleVoyage = async ({
                                                            accountId,
                                                            voyageId,
                                                            taskId,
                                                            executionId,
                                                            status,
                                                            completionTime,
                                                        }: IPropsChangeExecutionStatus,
                                                        onError?: OnShowError
): Promise<ITaskExecutionSingleVoyage> => {
    const url = `${BASE_URL}/${accountId}/single-voyages/${voyageId}/tasks/${taskId}/executions/${executionId}/status/${status}`
    return await apiRequestWithAuth<ITaskExecutionSingleVoyage>({
        url,
        options: {
            method: "PATCH",
            body: JSON.stringify({completionTime: completionTime ?? null}),
            headers: {
                'Content-type': 'application/json'
            }
        },
        onError
    });

}

/**
 * Closes every execution of the task that is still open, leaving closed ones alone.
 * taskId accepts both a main task and an individual subtask.
 */
export const closeTaskSingleVoyage = async ({
                                                accountId,
                                                voyageId,
                                                taskId,
                                                resolution,
                                                completionTime,
                                            }: IPropsCloseTaskSingleVoyage,
                                            onError?: OnShowError
): Promise<ITaskSingleVoyage> => {
    const url = `${BASE_URL}/${accountId}/single-voyages/${voyageId}/tasks/${taskId}/close`
    return await apiRequestWithAuth<ITaskSingleVoyage>({
        url,
        options: {
            method: "POST",
            body: JSON.stringify({
                resolution,
                completionTime: completionTime ?? null,
            }),
            headers: {
                'Content-type': 'application/json'
            }
        },
        onError
    });

}

export const uploadTaskFileSingleVoyage = async ({
                                                     accountId,
                                                     voyageId,
                                                     taskId,
                                                     executionId,
                                                     file,
                                                 }: IPropsUploadTaskFileSingleVoyage,
                                                 onError?: OnShowError
): Promise<string> => {
    const url = `${BASE_URL}/${accountId}/single-voyages/${voyageId}/tasks/${taskId}/executions/${executionId}/files`
    const body = new FormData()
    body.append('file', file)
    return await apiRequestWithAuth<string>({
        url,
        options: {
            method: "POST",
            body,
        },
        onError
    });

}

export const deleteTaskFileSingleVoyage = async ({
                                                     accountId,
                                                     voyageId,
                                                     taskId,
                                                     executionId,
                                                     fileId,
                                                 }: IPropsDeleteTaskFileSingleVoyage,
                                                 onError?: OnShowError
): Promise<string> => {
    const url = `${BASE_URL}/${accountId}/single-voyages/${voyageId}/tasks/${taskId}/executions/${executionId}/files/${fileId}`
    return await apiRequestWithAuth<string>({
        url,
        options: {
            method: "DELETE",
        },
        onError
    });

}

// Only editable plan fields belong in a task update.
const taskPlanBody = (task: Omit<TaskCreationSingleVoyage, 'accountId'>) => ({
    id: task.id,
    title: task.title,
    description: task.description,
    type: task.type,
    taskConfigTemplateId: task.taskConfigTemplateId,
    planCompletionTime: task.planCompletionTime,
    waypointIds: task.waypointIds,
    subtasks: (task.subtasks ?? []).map(child => ({
        id: child.id,
        title: child.title,
        description: child.description,
        type: child.type,
        taskConfigTemplateId: child.taskConfigTemplateId,
        planCompletionTime: child.planCompletionTime,
        waypointIds: child.waypointIds,
    })),
})
